export interface TimeValues {
  years: number
  months: number
  days: number
  hours: number
  minutes: number
}

/**
 * Calculate time saved based on initial values, starting date, and growth rate
 * @param initialValues - The initial time values when counting started
 * @param startingDate - The date when the counting started (ISO string)
 * @param growthRate - The percentage of elapsed time to add (e.g., 100 means 1:1, 50 means half)
 * @returns Updated time values with growth applied
 */
export const calculateTimeSaved = (initialValues: TimeValues, startingDate: string, growthRate: number): TimeValues => {
  // Parse the starting date
  const startDate = new Date(startingDate)
  const now = new Date()

  // Calculate elapsed time in milliseconds
  const elapsed = now.getTime() - startDate.getTime()

  // Convert elapsed time to minutes
  const elapsedMinutes = elapsed / (1000 * 60)

  // Calculate additional minutes based on growth rate
  const additionalMinutes = elapsedMinutes * (growthRate / 100)

  // Convert initial values to total minutes
  const initialTotalMinutes =
    initialValues.minutes +
    initialValues.hours * 60 +
    initialValues.days * 24 * 60 +
    initialValues.months * 30 * 24 * 60 + // Approximate month as 30 days
    initialValues.years * 365 * 24 * 60 // Approximate year as 365 days

  // Add the additional minutes
  const totalMinutes = initialTotalMinutes + additionalMinutes

  // Convert back to time components
  let remainingMinutes = Math.floor(totalMinutes)

  const years = Math.floor(remainingMinutes / (365 * 24 * 60))
  remainingMinutes -= years * 365 * 24 * 60

  const months = Math.floor(remainingMinutes / (30 * 24 * 60))
  remainingMinutes -= months * 30 * 24 * 60

  const days = Math.floor(remainingMinutes / (24 * 60))
  remainingMinutes -= days * 24 * 60

  const hours = Math.floor(remainingMinutes / 60)
  remainingMinutes -= hours * 60

  const minutes = remainingMinutes

  return {
    years,
    months,
    days,
    hours,
    minutes,
  }
}
